import { TbHighlight, TbBook, TbBulb, TbUser, TbTag } from 'react-icons/tb';
import { HiOutlineTranslate, HiOutlineX, HiOutlineChevronUp } from 'react-icons/hi';
import { useEffect, useState } from 'react';
import { TranslationModal } from './TranslationModal';
import { API_BASE_URL } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

export const ContextToolbar = ({ selection, onClear, onCreateNote, onCreateEntity }) => {
  const [translation, setTranslation] = useState(null);
  const [translationData, setTranslationData] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const isSingleWord = selection.startWord === selection.endWord;

  useEffect(() => {
    if (isSingleWord && selection.text) {
       setTranslation('Loading...');
       fetch(`${API_BASE_URL}/api/translate`, {
           method: 'POST',
           headers: { 'Content-Type': 'application/json' },
           body: JSON.stringify({ text: selection.text })
       })
       .then(res => res.json())
       .then(data => {
           setTranslation(data.translation);
           setTranslationData(data.markdown); // Use markdown property from response
       })
       .catch(() => {
           setTranslation('Error');
           setTranslationData(null);
       });
       setIsExpanded(false); // Reset expansion on new word
    } else {
       setTranslation(null);
       setTranslationData(null);
       setIsExpanded(false);
    }
  }, [selection, isSingleWord]);

  if (!selection.text) return null;

  return (
    <>
      <div
        className="w-full shrink-0 z-40 border-t animate-in slide-in-from-bottom-4 duration-200"
        style={{ background: 'var(--card)', borderColor: 'var(--card-border)', boxShadow: 'var(--shadow-2)' }}
      >
         <div className="content-width mx-auto px-4 w-full">

            {isSingleWord ? (
              /* Single Word - Translation Bar */
              <div className="flex items-center justify-between h-14 gap-4">
                 <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-1.5 rounded-lg" style={{ background: 'var(--accent-tint)', color: 'var(--accent)' }}>
                       <HiOutlineTranslate className="w-5 h-5"/>
                    </div>
                    <div className="flex items-baseline gap-2 truncate">
                       <span className="font-bold" style={{ color: 'var(--foreground)' }}>{selection.text}</span>
                       <span className="text-sm" style={{ color: 'var(--foreground-muted)' }}>→</span>
                       <span className="font-medium truncate" style={{ color: 'var(--foreground-muted)' }} dir="auto">
                          {translation ? translation.replace('ترجمه: ', '') : 'Loading...'}
                       </span>
                    </div>
                 </div>

                 <div className="flex items-center gap-2 shrink-0">
                    <ActionButton icon={<TbUser className="w-5 h-5"/>} label="Character" color="purple" compact onClick={() => onCreateEntity('character')} />
                    <ActionButton icon={<TbTag className="w-5 h-5"/>} label="Entity" color="pink" compact onClick={() => onCreateEntity('concept')} />
                    <div className="w-px h-6 mx-1" style={{ background: 'var(--card-border)' }}/>
                    <button
                      onClick={() => setIsExpanded(true)}
                      className="button-ghost p-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                    >
                      <span>More</span>
                      <HiOutlineChevronUp className="w-4 h-4"/>
                    </button>
                    <div className="w-px h-6 mx-1" style={{ background: 'var(--card-border)' }}/>
                    <button
                      onClick={onClear}
                      className="button-ghost p-2 rounded-lg transition-colors"
                    >
                      <HiOutlineX className="w-5 h-5"/>
                    </button>
                 </div>
              </div>
            ) : (
              /* Multi Word - Note Taking Bar */
              <div className="flex items-center justify-between h-16 gap-4">
                 <div className="flex items-center gap-3">
                    <span className="text-sm font-medium hidden sm:inline-block" style={{ color: 'var(--foreground-muted)' }}>Create Note:</span>
                    <div className="flex items-center gap-2">
                       <ActionButton icon={<TbHighlight className="w-5 h-5"/>} label="Highlight" color="yellow" onClick={() => onCreateNote('highlight')} />
                       <ActionButton icon={<TbBook className="w-5 h-5"/>} label="Define" color="blue" onClick={() => onCreateNote('definition')} />
                       <ActionButton icon={<TbBulb className="w-5 h-5"/>} label="Example" color="green" onClick={() => onCreateNote('example')} />
                       <div className="w-px h-6 mx-1" style={{ background: 'var(--card-border)' }}/>
                       <ActionButton icon={<TbUser className="w-5 h-5"/>} label="Character" color="purple" onClick={() => onCreateEntity('character')} />
                       <ActionButton icon={<TbTag className="w-5 h-5"/>} label="Entity" color="pink" onClick={() => onCreateEntity('concept')} />
                    </div>
                 </div>

                 <button
                   onClick={onClear}
                   className="button-ghost p-2 rounded-lg transition-colors"
                 >
                   <HiOutlineX className="w-5 h-5"/>
                 </button>
              </div>
            )}
         </div>
      </div>

      {/* Expanded Translation Modal */}
      <TranslationModal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        word={selection.text}
        translation={translation}
        markdown={translationData}
      />
    </>
  );
};

const ActionButton = ({ icon, label, color, onClick, compact = false }) => {
  const { mode } = useTheme();

  const colors = mode === 'dark'
    ? {
        yellow: "bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50",
        blue: "bg-blue-900/30 text-blue-400 hover:bg-blue-900/50",
        green: "bg-green-900/30 text-green-400 hover:bg-green-900/50",
        purple: "bg-purple-900/30 text-purple-400 hover:bg-purple-900/50",
        pink: "bg-pink-900/30 text-pink-400 hover:bg-pink-900/50",
      }
    : {
        yellow: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
        blue: "bg-blue-100 text-blue-700 hover:bg-blue-200",
        green: "bg-green-100 text-green-700 hover:bg-green-200",
        purple: "bg-purple-100 text-purple-700 hover:bg-purple-200",
        pink: "bg-pink-100 text-pink-700 hover:bg-pink-200",
      };

  return (
    <button
      onClick={onClick}
      title={label}
      className={`flex items-center gap-2 rounded-lg text-sm font-medium transition-colors ${compact ? 'p-2' : 'px-4 py-2'} ${colors[color]}`}
    >
      {icon}
      {!compact && <span>{label}</span>}
    </button>
  );
};
